import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Alert } from '@iot-dashboard/shared';
import { useAlerts, useResolveAlert } from '../../api/hooks';
import { DataTable, type Column } from '../../components/DataTable';
import { Tabs } from '../../components/Form';
import { Panel } from '../../components/Panel';
import { SeverityBadge } from '../../components/SeverityBadge';
import { ErrorState, Loading, errorMessage } from '../../components/States';
import { TimeAgo } from '../../components/TimeAgo';
import { useToast } from '../../components/Toast';
import { PageHeader } from '../../layout/PageHeader';
import { useTenantContext } from '../../layout/TenantLayout';
import { formatDateTime, formatDuration, minutesSince } from '../../lib/format';

export function AlertsPage() {
  const { tenantId, base, canConfigure } = useTenantContext();
  const [tab, setTab] = useState<'open' | 'resolved'>('open');
  const open = useAlerts(tenantId, 'open');
  const resolved = useAlerts(tenantId, 'resolved');
  const resolve = useResolveAlert(tenantId);
  const toast = useToast();
  const query = tab === 'open' ? open : resolved;

  const ack = async (a: Alert) => {
    try {
      await resolve.mutateAsync(a.alertId);
      toast.push('Alert resolved', 'success');
    } catch (err) {
      toast.push(errorMessage(err), 'error');
    }
  };

  const columns: Column<Alert>[] = [
    { key: 'severity', header: 'Severity', sortValue: (a) => a.severity, render: (a) => <SeverityBadge severity={a.severity} /> },
    { key: 'message', header: 'Alert', primary: true, sortValue: (a) => a.message, render: (a) => (
      <div>
        <div className="cell-primary">{a.message}</div>
        <div className="cell-sub">{a.ruleName}</div>
      </div>
    ) },
    { key: 'device', header: 'Sensor', sortValue: (a) => a.deviceName, render: (a) => <Link to={`${base}/sensors/${encodeURIComponent(a.deviceId)}`}>{a.deviceName}</Link> },
    { key: 'opened', header: 'Opened', sortValue: (a) => a.openedAt, render: (a) => <span title={formatDateTime(a.openedAt)}><TimeAgo iso={a.openedAt} /></span> },
    tab === 'open'
      ? { key: 'duration', header: 'Duration', sortValue: (a) => a.openedAt, render: (a) => formatDuration(minutesSince(a.openedAt)) }
      : { key: 'resolved', header: 'Resolved', sortValue: (a) => a.resolvedAt ?? '', render: (a) => <div><TimeAgo iso={a.resolvedAt} /><div className="cell-sub">{a.resolvedBy === 'system' ? 'auto-cleared' : a.resolvedBy}</div></div> },
    ...(tab === 'open' && canConfigure
      ? [{ key: 'actions', header: '', render: (a: Alert) => <button type="button" className="btn btn--sm" disabled={resolve.isPending} onClick={() => ack(a)}>Resolve</button> } satisfies Column<Alert>]
      : []),
  ];

  return (
    <>
      <PageHeader title="Alerts" subtitle="Alerts open automatically when an alert profile matches and clear when the condition passes." actions={<Link to={`${base}/alert-profiles`} className="btn">Alert profiles</Link>} />
      <Panel flush title={<Tabs tabs={[{ key: 'open', label: 'Open', count: open.data?.length }, { key: 'resolved', label: 'History', count: resolved.data?.length }]} active={tab} onChange={setTab} />}>
        {query.isPending ? <Loading /> : query.isError ? <ErrorState error={query.error} retry={() => query.refetch()} /> : (
          <DataTable
            columns={columns}
            rows={query.data}
            rowKey={(a) => a.alertId}
            defaultSort={{ key: 'opened', dir: 'desc' }}
            refetching={query.isFetching}
            empty={<div className="empty"><h3>{tab === 'open' ? 'No open alerts' : 'No resolved alerts yet'}</h3></div>}
          />
        )}
      </Panel>
    </>
  );
}
